/**
 * Google Analytics 4 (GA4) utility functions for tracking events
 * Requires Google Analytics base script to be loaded in index.html
 */

// Check if gtag is available
const isGAAvailable = () => {
  return typeof window !== 'undefined' && typeof window.gtag === 'function';
};

/**
 * Track PageView event
 * @param {string} pagePath - Optional page path to track
 * @param {string} pageTitle - Optional page title
 */
export const trackPageView = (pagePath, pageTitle) => {
  if (!isGAAvailable()) {
    return;
  }

  try {
    const params = {};
    if (pagePath) {
      params.page_path = pagePath;
    }
    if (pageTitle) {
      params.page_title = pageTitle;
    }

    window.gtag('event', 'page_view', params);
  } catch (error) {
    console.error('Error tracking PageView:', error);
  }
};

/**
 * Track ViewItem event (when viewing a product)
 * @param {Object} product - Product object with id, name, price, etc.
 */
export const trackViewItem = (product) => {
  if (!isGAAvailable()) {
    return;
  }

  if (!product) {
    console.warn('Product data is required for ViewItem event');
    return;
  }

  try {
    const itemData = {
      currency: 'TRY',
      value: product.price,
      items: [{
        item_id: product._id || product.id,
        item_name: product.name,
        item_category: 'Product',
        price: product.price,
        quantity: 1,
      }],
    };

    window.gtag('event', 'view_item', itemData);
  } catch (error) {
    console.error('Error tracking ViewItem:', error);
  }
};

/**
 * Track AddToCart event
 * @param {Object} product - Product object
 * @param {number} quantity - Quantity added to cart
 */
export const trackAddToCart = (product, quantity = 1) => {
  if (!isGAAvailable()) {
    return;
  }

  if (!product) {
    console.warn('Product data is required for AddToCart event');
    return;
  }

  try {
    const cartData = {
      currency: 'TRY',
      value: product.price * quantity,
      items: [{
        item_id: product._id || product.id,
        item_name: product.name,
        item_category: 'Product',
        price: product.price,
        quantity: quantity,
      }],
    };

    window.gtag('event', 'add_to_cart', cartData);
  } catch (error) {
    console.error('Error tracking AddToCart:', error);
  }
};

/**
 * Track BeginCheckout event
 * @param {number} value - Total cart value
 * @param {number} numItems - Number of items in cart
 * @param {Array} items - Array of cart items
 */
export const trackBeginCheckout = (value, numItems, items = []) => {
  if (!isGAAvailable()) {
    return;
  }

  try {
    // Format items for GA4
    const formattedItems = items.map(item => ({
      item_id: item.id || item.productId?._id || item.productId?.id,
      item_name: item.productId?.name || 'Product',
      item_category: 'Product',
      price: item.item_price || item.productId?.price || 0,
      quantity: item.quantity || 0,
    }));

    const checkoutData = {
      currency: 'TRY',
      value: value,
      items: formattedItems,
    };

    window.gtag('event', 'begin_checkout', checkoutData);
  } catch (error) {
    console.error('Error tracking BeginCheckout:', error);
  }
};

/**
 * Track Purchase event
 * @param {Object} orderData - Order data with id, value, currency, items
 */
export const trackPurchase = (orderData) => {
  if (!isGAAvailable()) {
    return;
  }

  if (!orderData) {
    console.warn('Order data is required for Purchase event');
    return;
  }

  try {
    const items = orderData.items || orderData.orderItems || [];
    
    // Format items for GA4
    const formattedItems = items.map(item => {
      const productId = item.product?._id || item.product?.id || item.productId?._id || item.productId?.id;
      const price = item.price || item.product?.price || item.productId?.price || 0;
      const quantity = item.quantity || 0;
      const productName = item.product?.name || item.productId?.name || 'Product';
      
      return {
        item_id: productId,
        item_name: productName,
        item_category: 'Product',
        price: price,
        quantity: quantity,
      };
    }).filter(item => item.item_id); // Filter out items without IDs

    const purchaseData = {
      transaction_id: orderData.orderId || orderData._id || orderData.orderNumber || '',
      value: orderData.total || orderData.value,
      currency: orderData.currency || 'TRY',
      items: formattedItems,
    };

    window.gtag('event', 'purchase', purchaseData);
  } catch (error) {
    console.error('Error tracking Purchase:', error);
  }
};

